import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import BrowseCategories from './components/BrowseCategories';
import ExploreProducts from './components/ExploreProducts';
import NewArrivals from './components/NewArrivals';
import ServicesBanner from './components/ServicesBanner';
import Footer from './components/Footer';
import AboutUs from './components/AboutUs';
import ContactUs from './components/ContactUs';
import ProductDetail from './components/ProductDetail';
import Login from './components/Login';
import Register from './components/Register';
import AdminPanel from './components/AdminPanel';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import ThankYou from './components/ThankYou';
import UserProfile from './components/UserProfile';
import OrderHistory from './components/OrderHistory';
import RmaModal from './components/RmaModal';
import WishList from './components/WishList';
import WarrantyPolicy from './components/WarrantyPolicy';

function App() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [user, setUser] = useState(null);
  const [isRmaOpen, setIsRmaOpen] = useState(false);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
      window.scrollTo(0, 0);
    };

    const loadSession = () => {
      const session = localStorage.getItem('user_session');
      if (session) {
        setUser(JSON.parse(session));
      } else {
        setUser(null);
      }
    };

    // Refresh session from server to get latest user data (e.g. isB2B flag)
    const refreshSession = async () => {
      const session = localStorage.getItem('user_session');
      if (!session) return;
      try {
        const parsed = JSON.parse(session);
        if (!parsed.email) return;
        const res = await fetch('/api/auth/profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: parsed.email })
        });
        if (res.ok) {
          const data = await res.json();
          if (data.user) {
            const updated = { ...parsed, ...data.user };
            localStorage.setItem('user_session', JSON.stringify(updated));
            setUser(updated);
            window.dispatchEvent(new Event('auth_change'));
          }
        }
      } catch (err) {
        console.error('Session refresh failed:', err);
      }
    };

    loadSession();
    refreshSession();

    const handleOpenRma = () => {
      console.log('open_rma_modal event received in App.js. Setting isRmaOpen to true.');
      setIsRmaOpen(true);
    };

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);
    window.addEventListener('storage', loadSession);
    window.addEventListener('auth_change', loadSession);
    window.addEventListener('open_rma_modal', handleOpenRma);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
      window.removeEventListener('storage', loadSession);
      window.removeEventListener('auth_change', loadSession);
      window.removeEventListener('open_rma_modal', handleOpenRma);
    };
  }, []);

  const isAboutPage = currentPath === '/about-us' || window.location.hash === '#/about-us' || window.location.hash === '#/about';
  const isContactPage = currentPath === '/contact-us' || window.location.hash === '#/contact-us' || window.location.hash === '#/contact';
  const isLoginPage = currentPath === '/login' || window.location.hash === '#/login';
  const isRegisterPage = currentPath === '/register' || window.location.hash === '#/register' || currentPath === '/signup' || window.location.hash === '#/signup';
  const isAdminPage = (currentPath === '/admin' || window.location.hash === '#/admin') && user && user.email === 'smarttelecom.nl@gmail.com';
  const isCartPage = currentPath === '/cart' || window.location.hash === '#/cart';
  const isCheckoutPage = currentPath === '/checkout' || window.location.hash === '#/checkout';
  const isThankYouPage = currentPath === '/thank-you' || window.location.hash === '#/thank-you';
  const isProfilePage = currentPath === '/profile' || window.location.hash === '#/profile' || currentPath === '/my-account' || window.location.hash === '#/my-account';
  const isOrderHistoryPage = currentPath === '/order-history' || window.location.hash === '#/order-history' || window.location.hash === '#/orders';
  const isWishListPage = currentPath === '/wish-list' || window.location.hash === '#/wish-list' || currentPath === '/wishlist' || window.location.hash === '#/wishlist';
  const isWarrantyPage = currentPath === '/warranty-policy' || window.location.hash === '#/warranty-policy' || currentPath === '/warranty' || window.location.hash === '#/warranty';
  
  // Parse product details page path: e.g. /product/1 or /product/60d5ec...
  const isProductPage = currentPath.startsWith('/product/') || window.location.hash.startsWith('#/product/');
  let productId = "";
  if (isProductPage) {
    const activeRouteStr = currentPath.startsWith('/product/') ? currentPath : window.location.hash.replace('#', '');
    const parts = activeRouteStr.split('/');
    productId = parts[parts.length - 1] || "";
  }

  useEffect(() => {
    if (isProductPage) return;

    let title = 'Smart Telecom Store | Premium Mobile & Telecom Accessories';
    let description = 'Welcome to Smart Telecom Store. Explore our wide range of premium smartphones, mobile accessories, chargers, cables, and telecom solutions.';
    let keywords = 'smart telecom store, mobile accessories, chargers, cables, smartphones, telecom products';

    if (isAboutPage) {
      title = 'About Us | Smart Telecom Store';
      description = 'Learn more about Smart Telecom Store, our mission, values, and how we provide the best mobile and telecom accessories to our clients.';
      keywords = 'about smart telecom, telecom shop history, our mission, mobile accessories supplier';
    } else if (isContactPage) {
      title = 'Contact Us | Smart Telecom Store';
      description = 'Get in touch with Smart Telecom Store. Contact us for inquiries, support, feedback, or business collaborations.';
      keywords = 'contact smart telecom, customer support, email, phone number, address';
    } else if (isLoginPage) {
      title = 'Login | Smart Telecom Store';
      description = 'Log in to your Smart Telecom Store account to manage your orders, wishlist, and profile.';
      keywords = 'login, sign in, smart telecom login';
    } else if (isRegisterPage) {
      title = 'Register | Smart Telecom Store';
      description = 'Create a new account at Smart Telecom Store to start shopping premium mobile accessories and telecom products.';
      keywords = 'register, sign up, create account, smart telecom signup';
    } else if (isAdminPage) {
      title = 'Admin Dashboard | Smart Telecom Store';
      description = 'Administrative control panel for managing products, categories, orders, and users at Smart Telecom Store.';
      keywords = 'admin dashboard, management';
    } else if (isCartPage) {
      title = 'Shopping Cart | Smart Telecom Store';
      description = 'View the items in your shopping cart, update quantities, and proceed to checkout at Smart Telecom Store.';
      keywords = 'shopping cart, checkout, cart, telecom items';
    } else if (isCheckoutPage) {
      title = 'Checkout | Smart Telecom Store';
      description = 'Secure checkout page. Provide your shipping and billing details to complete your order at Smart Telecom Store.';
      keywords = 'secure checkout, pay order, shipping details';
    } else if (isThankYouPage) {
      title = 'Thank You for Your Order | Smart Telecom Store';
      description = 'Thank you for shopping with Smart Telecom Store. Your order has been placed successfully.';
      keywords = 'thank you, order success, confirmation';
    } else if (isProfilePage) {
      title = 'My Profile | Smart Telecom Store';
      description = 'Manage your profile and account settings at Smart Telecom Store.';
      keywords = 'profile, my account, smart telecom store';
    } else if (isOrderHistoryPage) {
      title = 'Order History | Smart Telecom Store';
      description = 'View your recent order history and transactions at Smart Telecom Store.';
      keywords = 'orders, order history, receipts, smart telecom store';
    } else if (isWarrantyPage) {
      title = 'Warranty Policy | Smart Telecom Store';
      description = 'Read the warranty and RMA policy of Recent Inspiration. 2-year warranty on all products and terms of coverage.';
      keywords = 'warranty policy, rma policy, 2-year warranty, zero pixel warranty, recent inspiration';
    }

    document.title = title;

    let metaDescTag = document.querySelector('meta[name="description"]');
    if (!metaDescTag) {
      metaDescTag = document.createElement('meta');
      metaDescTag.name = 'description';
      document.head.appendChild(metaDescTag);
    }
    metaDescTag.content = description;

    let metaKeywordsTag = document.querySelector('meta[name="keywords"]');
    if (!metaKeywordsTag) {
      metaKeywordsTag = document.createElement('meta');
      metaKeywordsTag.name = 'keywords';
      document.head.appendChild(metaKeywordsTag);
    }
    metaKeywordsTag.content = keywords;
  }, [currentPath, isAboutPage, isContactPage, isLoginPage, isRegisterPage, isAdminPage, isCartPage, isCheckoutPage, isThankYouPage, isProductPage, isProfilePage, isOrderHistoryPage, isWarrantyPage]);

  const renderContent = () => {
    if (isAboutPage) return <AboutUs />;
    if (isContactPage) return <ContactUs />;
    if (isLoginPage) return <Login />;
    if (isRegisterPage) return <Register />;
    if (isAdminPage) return <AdminPanel />;
    if (isProductPage) return <ProductDetail productId={productId} />;
    if (isCartPage) return <Cart />;
    if (isCheckoutPage) return <Checkout />;
    if (isThankYouPage) return <ThankYou />;
    if (isProfilePage) return <UserProfile />;
    if (isOrderHistoryPage) return <OrderHistory />;
    if (isWishListPage) return <WishList />;
    if (isWarrantyPage) return <WarrantyPolicy />;
    return (
      <>
        <HeroSection />
        <BrowseCategories />
        <ExploreProducts />
        <NewArrivals />
        <ServicesBanner />
      </>
    );
  };

  return (
    <div className="App">
      <Header />
      {renderContent()}
      <Footer />
      <RmaModal isOpen={isRmaOpen} onClose={() => setIsRmaOpen(false)} />
      {/* Floating WhatsApp Container */}
      <div className="whatsapp-float-container">
        <svg viewBox="0 0 100 100" className="whatsapp-text-ring">
          <path id="whatsapp-text-path" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" fill="none" />
          <text fill="#be1e2d" fontSize="9" fontWeight="700" letterSpacing="1.5">
            <textPath href="#whatsapp-text-path" startOffset="0%">
              • CHAT WITH US • HELP LINE • SMART TELECOM
            </textPath>
          </text>
        </svg>
        <a
          href="https://wa.me/31687748358"
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-float-btn"
          aria-label="Contact us on WhatsApp"
        >
          <svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor">
            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.724-1.455L0 24zm6.59-4.846c1.66.986 3.296 1.506 4.905 1.508 5.482.002 9.948-4.468 9.95-9.953.003-2.657-1.02-5.153-2.883-7.019C16.762 1.82 14.269.789 11.618.789c-5.485 0-9.95 4.466-9.952 9.953-.001 1.83.479 3.619 1.393 5.215l-.99 3.61 3.708-.973zm12.38-5.321c-.305-.153-1.804-.89-2.083-.99-.279-.103-.483-.153-.686.153-.203.306-.787.99-.965 1.193-.177.202-.355.228-.66.076-.305-.153-1.288-.475-2.455-1.517-.908-.81-1.52-1.81-1.698-2.115-.178-.306-.019-.471.133-.622.137-.137.305-.355.457-.533.153-.178.203-.305.305-.509.102-.203.05-.381-.025-.533-.076-.153-.686-1.653-.94-2.26-.247-.594-.5-.513-.686-.523-.178-.01-.381-.01-.584-.01-.203 0-.533.076-.813.381-.28.305-1.067 1.042-1.067 2.541 0 1.499 1.092 2.946 1.244 3.149.153.203 2.15 3.284 5.207 4.602.727.314 1.294.502 1.737.643.73.232 1.394.2 1.917.12.583-.087 1.804-.737 2.058-1.45.254-.713.254-1.323.178-1.45-.076-.127-.28-.203-.585-.356z"/>
          </svg>
        </a>
      </div>
    </div>
  );
}

export default App;



